import { useQueryParams, useNavigate } from 'raviger';
import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import useAuth0Feathers from '../../hooks/useAuth0Feathers';
import Auth0ResetPasswordPage from './Auth0ResetPasswordPage';
import Auth0ConfirmEmailPage from './Auth0ConfirmEmailPage';
import EmailVerificationDialog from '../../Components/Common/EmailVerificationDialog';
import { Box, CircularProgress, Typography } from '@mui/material';
import appBG from '../../assets/appBG.jpg';
import { makeStyles } from '@mui/styles';
import { feathersClient } from '../../api/client';

const useStyles = makeStyles(() => ({
    background: {
        backgroundImage: `url(${appBG})`,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundAttachment: 'fixed',
        minHeight: "100vh",
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    }
}));

const Auth0LandingPage = () => {
    const classes = useStyles();
    const urlParams = useQueryParams();
    const navigate = useNavigate();
    const [action, setAction] = useState("");
    const [loading, setLoading] = useState(true);
    const [showEmailDialog, setShowEmailDialog] = useState(false);
    const [verificationCode, setVerificationCode] = useState('');
    const [resetToken, setResetToken] = useState('')
    const hookFeathers = useAuth0Feathers();

    useEffect(() => {
        handleAuth0Action();
    }, []);

    const handleAuth0Action = async () => {
        try {
            const mode = urlParams[0].mode;
            const code = urlParams[0].code;
            const ticket = urlParams[0].ticket;
            const success = urlParams[0].success;
            const token = urlParams[0].token;

            console.log('🔍 Auth0 Landing Page:', { mode, code, ticket, success });

            // Password reset
            if (mode === 'resetPassword' && token) {
                console.log('🔐 Password reset flow');
                setResetToken(token); // ⬅️ SAVE TOKEN
                setAction('resetPassword');
                setLoading(false);
                return;
            }

            // Email verification - handle both success=true and ticket parameters
            if (success === 'true' || mode === 'verifyEmail' || ticket) {
                console.log('📧 Email verification flow detected');
                setAction('emailVerified');
                setLoading(false);
                return;
            }

            // Magic link authentication
            if (mode === 'magicLink' && code) {
                console.log('🔗 Magic link clicked');
                await handleMagicLinkAuth(code);
                return;
            }

            console.log('⚠️ No recognized action');
            navigate('/');

        } catch (error) {
            console.error('❌ Error:', error);
            navigate('/');
        } finally {
            setLoading(false);
        }
    };

    const handleMagicLinkAuth = async (code) => {
        try {
            console.log('🔗 Processing magic link...');

            // Check for stored email (same device)
            const storedEmail = window.localStorage.getItem('emailForSignIn');

            if (storedEmail) {
                console.log('✅ Same device - stored email:', storedEmail);
                await completeMagicLinkAuth(storedEmail, code);
            } else {
                console.log('⚠️ Different device - show email dialog');
                setVerificationCode(code);
                setShowEmailDialog(true);
                setLoading(false);
            }

        } catch (error) {
            console.error('❌ Magic link failed:', error);
            window.alert('אירעה שגיאה בתהליך ההתחברות');
            navigate('/');
        }
    };

    const handleEmailVerification = async (email) => {
        console.log('📧 User entered email:', email);

        try {
            await completeMagicLinkAuth(email, verificationCode);
            setShowEmailDialog(false);
        } catch (error) {
            console.error('❌ Email verification failed:', error);
            throw error;
        }
    };

    const completeMagicLinkAuth = async (email, code) => {
        console.log('🔐 Verifying magic link...');

        try {
            // Verify the magic link code
            const verifyResult = await feathersClient.service('send-auth0-signin-email').create({
                email: email,
                verificationCode: code
            });

            if (!verifyResult.success) {
                throw new Error(verifyResult.error || 'Verification failed');
            }

            console.log('✅ Magic link verified');
            console.log(verifyResult)

            // Authenticate with Feathers
            const authenticatedUser = await feathersClient.authenticate({
                strategy: 'auth0',
                access_token: verifyResult.access_token,
                username: verifyResult.username,
                phoneNumber: verifyResult.phoneNumber
            });

            console.log('✅ Authentication complete');

            //user login state
            hookFeathers.setIsLoggedIn(true);

            // Clear localStorage
            window.localStorage.removeItem('emailForSignIn');
            window.localStorage.removeItem('usernameForSignIn');
            window.localStorage.removeItem('phoneForSignIn');

            // Navigate
            const role = authenticatedUser?.email?.includes('zofim.org.il') ? 'tribe' : 'parent';

            if (!authenticatedUser?.hasChildren && role === 'parent') {
                navigate('/register/addchild');
            } else {
                navigate(role === 'tribe' ? '/tribe' : '/parent');
            }

        } catch (error) {
            console.error('❌ Auth failed:', error);
            throw error;
        }
    };


    if (loading) {
        return (
            <Box className={classes.background}>
                <Box textAlign="center">
                    <CircularProgress sx={{ color: '#ffffff', mb: 2 }} />
                    <Typography sx={{ color: '#ffffff', fontSize: '1.2rem' }}>
                        טוען...
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <>
            {action === 'resetPassword' && <Auth0ResetPasswordPage token={resetToken} />}
            {(action === 'verifyEmail' || action === 'emailVerified') && <Auth0ConfirmEmailPage verified={action === 'emailVerified'} />}

            {showEmailDialog && (
                <Box className={classes.background}>
                    <EmailVerificationDialog
                        open={showEmailDialog}
                        onVerify={handleEmailVerification}
                        onCancel={() => navigate('/')}
                    />
                </Box>
            )}
        </>
    );
};

export default Auth0LandingPage;